/**
 * OVERRIDE ObjectBrowserNav.jsx
 * REASON:
 * FILE VERSION: 18.10.1 volto main (2025-09-03)
 * PULL REQUEST:
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/899
 * DEVELOPER: reekitconcept
 * CHANGELOG: More button
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */

import React from 'react';
import { Button, Segment, Popup } from 'semantic-ui-react';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { getContentIcon } from '@plone/volto/helpers/Content/Content';
import config from '@plone/volto/registry';
import AppButton from '../../../../../components/Button/Button';

import rightArrowSVG from '@plone/volto/icons/right-key.svg';
import homeSVG from '@plone/volto/icons/home.svg';

const messages = defineMessages({
  browse: {
    id: 'Browse',
    defaultMessage: 'Browse',
  },
  select: {
    id: 'Select',
    defaultMessage: 'Select',
  },
  loadMoreResults: {
    id: 'Load More Results',
    defaultMessage: 'Load More Results',
  },
});

const ImageOrIcon = ({ item, size, iconSize, className }) => {
  const Image = config.getComponent({ name: 'Image' }).component;

  const [hideIcon, setHideIcon] = React.useState(false);
  const [hideImage, setHideImage] = React.useState(false);

  const onError = () => {
    // Remove broken image handler
    // This removes the broken image icon and sometimes a border around the image
    setHideImage(true);
  };
  const onLoad = () => {
    // This removes the icon when the image is loaded for the list view
    // We need this because the image does not completely cover the icon
    if (!className) {
      setHideIcon(true);
    }
  };

  const hasImage = item.image_scales?.[item.image_field];
  return (
    <div className={`iconWrapper ${className} ${hasImage ? 'has-image' : ''}`}>
      {hasImage ? (
        <Image
          item={item}
          className="previewImage"
          loading="lazy"
          sizes="250px"
          /* width + height practically mandatory, see https://github.com/plone/volto/issues/5096 */
          width={`${size}`}
          height={`${size}`}
          onError={onError}
          onLoad={onLoad}
          style={{ display: hideImage ? 'none' : 'block' }}
        />
      ) : (
        <div className="previewImage no-image" />
      )}
      <Icon
        className="typeIcon"
        name={getContentIcon(item['@type'], item.is_folderish)}
        size={`${iconSize}px`}
        style={{ display: hideIcon ? 'none' : 'block' }}
      />
    </div>
  );
};

// React's syntethic onDoubleClick does not work well together with onClick
// for some reason (double clicking on the icons don't trigger), so we create
// a LI component that just uses onClick and simulates the double click.
const Li = ({ onClick, onDoubleClick, children, ...rest }) => {
  const clickTimer = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
    };
  }, []);

  const singleClick = (evt) => {
    // clear any pending single‐click
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    // wait 200ms; if no dblclick arrives, fire single click
    clickTimer.current = setTimeout(() => {
      onClick && onClick(evt);
      clickTimer.current = null;
    }, 200);
  };
  const doubleClick = (evt) => {
    // dblclick happened — cancel single click
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    onDoubleClick && onDoubleClick(evt);
  };

  return (
    <li
      onClick={singleClick}
      onDoubleClick={doubleClick}
      // This can be overwritten, but it is here to avoid eslint warnings
      role="presentation"
      // This should be overwritten, but it is here to avoid eslint warnings
      aria-label={'Image'}
      {...rest}
    >
      {children}
    </li>
  );
};

const ObjectBrowserNav = ({
  currentSearchResults,
  selected,
  handleClickOnItem,
  handleDoubleClickOnItem,
  mode,
  view,
  navigateTo,
  isSelectable,
  showMoreButton,
  onLoadMore,
  isSearchMode,
}) => {
  const intl = useIntl();
  const isSelected = (item) => {
    let ret = false;
    if (selected && Array.isArray(selected)) {
      selected
        .filter((item) => item != null)
        .forEach((_item) => {
          if (flattenToAppURL(_item['@id']) === flattenToAppURL(item['@id'])) {
            ret = true;
          }
        });
    }
    return ret;
  };

  const IdOrPath = ({ item }) => {
    let path = flattenToAppURL(item['@id']);
    return !isSearchMode ? (
      <span className="itemId">{path.split('/').filter(Boolean).pop()}</span>
    ) : (
      <span className="itemPath">{path}</span>
    );
  };

  const getIsPrivate = (item) =>
    item.allowedRolesAndUsers && Array.isArray(item.allowedRolesAndUsers)
      ? item.allowedRolesAndUsers?.includes('Anonymous') === false
        ? true
        : false
      : undefined;

  return (
    <Segment
      className={`object-listing object-browser-nav ${view === 'icons' ? 'object-browser-nav-grid-view' : 'object-browser-nav-list-view'}`}
    >
      <ul>
        {currentSearchResults &&
          currentSearchResults.items.map((item) =>
            view === 'icons' ? (
              <Li
                role="presentation"
                aria-label={
                  item.is_folderish && mode === 'image'
                    ? `${intl.formatMessage(messages.browse)} ${item.title}`
                    : `${intl.formatMessage(messages.select)} ${item.title}`
                }
                key={item['@id']}
                title={`${item['@id']} (${item['@type']})`}
                className={cx('', {
                  'selected-item': isSelected(item),
                  'private-item': getIsPrivate(item),
                  disabled:
                    mode === 'image'
                      ? !config.settings.imageObjects.includes(item['@type']) &&
                        !item.is_folderish
                      : !isSelectable(item),
                })}
                onClick={() => handleClickOnItem(item)}
                onDoubleClick={() => handleDoubleClickOnItem(item)}
              >
                <div basic>
                  <ImageOrIcon
                    item={item}
                    size={128}
                    iconSize={45}
                    className={cx('iconWrapperGrid', {
                      'selected-item': isSelected(item),
                      'private-item': getIsPrivate(item),
                    })}
                  />
                  {/* )} */}
                </div>
                <span className="itemTitle">{item.title}</span>
                <IdOrPath item={item} />
              </Li>
            ) : (
              <li
                role="presentation"
                aria-label={
                  item.is_folderish && mode === 'image'
                    ? `${intl.formatMessage(messages.browse)} ${item.title}`
                    : `${intl.formatMessage(messages.select)} ${item.title}`
                }
                key={item['@id']}
                className={cx('', {
                  'selected-item': isSelected(item),
                  'private-item': getIsPrivate(item),
                  disabled:
                    mode === 'image'
                      ? !config.settings.imageObjects.includes(item['@type']) &&
                        !item.is_folderish
                      : !isSelectable(item),
                })}
                onClick={() => handleClickOnItem(item)}
                onDoubleClick={() => handleDoubleClickOnItem(item)}
              >
                <Popup
                  key={item['@id']}
                  content={
                    <>
                      <Icon name={homeSVG} size="18px" />
                      {flattenToAppURL(item['@id'])} ({item['@type']})
                    </>
                  }
                  trigger={<ImageOrIcon item={item} size={32} iconSize={24} />}
                />
                <div className="itemInfoWrapper">
                  <span className="itemTitle">{item.title}</span>
                  <IdOrPath item={item} />
                </div>
                {item.is_folderish && mode === 'image' && (
                  <Icon
                    className="right-arrow-icon"
                    name={rightArrowSVG}
                    size="24px"
                  />
                )}
                {item.is_folderish &&
                  (mode === 'link' || mode === 'multiple') && (
                    <div
                      role="presentation"
                      className="right-arrow-link-mode"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateTo(item['@id']);
                      }}
                    >
                      <Button.Group>
                        <Button
                          type="button"
                          basic
                          icon
                          aria-label={`${intl.formatMessage(messages.browse)} ${
                            item.title
                          }`}
                        >
                          <Icon
                            className="right-arrow-icon"
                            name={rightArrowSVG}
                            size="24px"
                          />
                        </Button>
                      </Button.Group>
                    </div>
                  )}
              </li>
            ),
          )}
      </ul>
      {showMoreButton && (
        <AppButton className="load-more" type="button" onPress={onLoadMore}>
          {intl.formatMessage(messages.loadMoreResults)}
        </AppButton>
      )}
    </Segment>
  );
};

export default ObjectBrowserNav;
